import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import App from "@/App";
import { getSessionToken, clearSessionToken } from "@/lib/api";
import { installChunkErrorReload, primeDeployVersion } from "@/lib/staleDeployGuard";

// STALE-DEPLOY-GUARD (2026-07-23): reload once if a lazy chunk 404s after a
// deploy, and remember the current bundle version so <DeployWatcher> can pick
// up a new deploy on the next navigation (fixes "works only after refresh").
installChunkErrorReload();
primeDeployVersion();

// Configure axios globally to include auth token in all requests
axios.interceptors.request.use(
  (config) => {
    const token = getSessionToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Keep withCredentials for cookie fallback
    config.withCredentials = true;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthRoute = ['/auth/login', '/auth/register', '/auth/google', '/auth/me', '/auth/logout'].some(r => url.includes(r));
      if (!isAuthRoute) {
        clearSessionToken();
        window.location.href = '/login';
        return new Promise(() => {});
      }
    }
    return Promise.reject(error);
  }
);

// Suppress "Script error" from third-party scripts (Tawk.to, etc.)
// These errors are harmless cross-origin errors that don't affect functionality
const originalWindowError = window.onerror;
window.onerror = function(message, source, lineno, colno, error) {
  // Suppress generic "Script error" from cross-origin scripts
  if (message === 'Script error.' || message === 'Script error') {
    return true;
  }
  // Suppress ResizeObserver loop errors - benign browser warning
  if (message && typeof message === 'string' && message.includes('ResizeObserver loop')) {
    return true;
  }
  if (originalWindowError) {
    return originalWindowError(message, source, lineno, colno, error);
  }
  return false;
};

// Also suppress in error event listener (for React error overlay)
window.addEventListener('error', function(event) {
  if (event.message === 'Script error.' || event.message === 'Script error') {
    event.stopImmediatePropagation();
    event.preventDefault();
    return true;
  }
  // Suppress ResizeObserver loop errors - benign browser warning
  if (event.message && event.message.includes('ResizeObserver loop')) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return true;
  }
}, true);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
